import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Users, Package, ShoppingBag, DollarSign, TrendingUp, AlertCircle, CheckCircle, XCircle, Eye, Ban, Star, Handshake, Crown, Trophy, Loader2, Copy, ExternalLink, Tag, Banknote, Wallet, Newspaper, MessageSquare, Mail, Truck, Download, Languages } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { AdminPressReleasesPanel } from '../components/AdminPressReleasesPanel';
import { AdminTestimonialsPanel } from '../components/AdminTestimonialsPanel';
import { AdminCampaignsPanel } from '../components/AdminCampaignsPanel';
import { AdminBoxtalShipDialog } from '../components/AdminBoxtalShipDialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminDashboard = () => {
  const { t, language, getLocalizedName } = useLanguage();
  const { token } = useAuth();
  const navigate = useNavigate();

  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [products, setProducts] = useState([]);
  const [translatingId, setTranslatingId] = useState(null);
  const [bulkJob, setBulkJob] = useState(null);
  const pollTimerRef = useRef(null);

  useEffect(() => () => clearTimeout(pollTimerRef.current), []);
  const [orders, setOrders] = useState([]);
  const [boxtalOrder, setBoxtalOrder] = useState(null);
  const [webhooksBusy, setWebhooksBusy] = useState(false);
  const [csvBusy, setCsvBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, usersRes, vendorsRes, productsRes, ordersRes] = await Promise.all([
          axios.get(`${API}/admin/stats`, authHeaders),
          axios.get(`${API}/admin/users`, authHeaders),
          axios.get(`${API}/admin/vendors`, authHeaders),
          axios.get(`${API}/admin/products`, authHeaders),
          axios.get(`${API}/orders`, authHeaders)
        ]);

        setStats(statsRes.data);
        setUsers(usersRes.data);
        setVendors(vendorsRes.data);
        setProducts(productsRes.data);
        setOrders(ordersRes.data);
      } catch (error) {
        console.error('Error:', error);
        if (error.response?.status === 403) {
          toast.error(language === 'fr' ? 'Accès refusé' : 'Access denied');
          navigate('/');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token, navigate, language]);

  const handleVendorStatus = async (vendorId, status) => {
    try {
      await axios.put(`${API}/admin/vendors/${vendorId}/status?status=${status}`, {}, authHeaders);
      setVendors(vendors.map(v => v.id === vendorId ? { ...v, status } : v));
      toast.success(language === 'fr' ? 'Statut mis à jour' : 'Status updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    }
  };

  const handleProductStatus = async (productId, status) => {
    try {
      await axios.put(`${API}/admin/products/${productId}/status?status=${status}`, {}, authHeaders);
      setProducts(products.map(p => p.id === productId ? { ...p, status } : p));
      toast.success(language === 'fr' ? 'Statut mis à jour' : 'Status updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    }
  };

  const handleToggleFeatured = async (productId, isFeatured) => {
    try {
      await axios.put(`${API}/admin/products/${productId}/featured?is_featured=${isFeatured}`, {}, authHeaders);
      setProducts(products.map(p => p.id === productId ? { ...p, is_featured: isFeatured } : p));
      toast.success(language === 'fr' ? 'Produit mis en avant' : 'Product featured');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    }
  };

  const handleTranslateProduct = async (productId) => {
    setTranslatingId(productId);
    try {
      const res = await axios.post(`${API}/products/${productId}/translate`, {}, authHeaders);
      toast.success(language === 'fr'
        ? `Fiche traduite en ${res.data.count} langues ✓`
        : `Listing translated into ${res.data.count} languages ✓`);
      setProducts((prev) => prev.map((p) => p.id === productId ? { ...p, ai_translated_at: new Date().toISOString() } : p));
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'fr' ? 'Erreur de traduction' : 'Translation error'));
    } finally {
      setTranslatingId(null);
    }
  };

  const pollBulkStatus = async () => {
    try {
      const r = await axios.get(`${API}/products/translate-all/status`, authHeaders);
      setBulkJob(r.data);
      if (r.data?.status === 'running') {
        pollTimerRef.current = setTimeout(pollBulkStatus, 4000);
      } else if (r.data?.status === 'completed') {
        toast.success(language === 'fr'
          ? `Catalogue traduit : ${r.data.done} fiche(s) ✓${r.data.failed ? ` (${r.data.failed} échec(s))` : ''}`
          : `Catalog translated: ${r.data.done} listing(s) ✓${r.data.failed ? ` (${r.data.failed} failed)` : ''}`);
        try {
          const res = await axios.get(`${API}/admin/products`, authHeaders);
          setProducts(res.data);
        } catch { /* ignore */ }
      }
    } catch { /* ignore */ }
  };

  const handleTranslateAll = async () => {
    try {
      const res = await axios.post(`${API}/products/translate-all`, {}, authHeaders);
      if (res.data.total === 0) {
        toast.info(language === 'fr' ? 'Tout le catalogue est déjà traduit ✓' : 'The whole catalog is already translated ✓');
        return;
      }
      toast.success(language === 'fr'
        ? `Traduction de ${res.data.total} fiche(s) lancée…`
        : `Translating ${res.data.total} listing(s)…`);
      setBulkJob({ status: 'running', total: res.data.total, done: 0 });
      pollTimerRef.current = setTimeout(pollBulkStatus, 4000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    }
  };

  const exportOrdersCsv = async () => {
    setCsvBusy(true);
    try {
      const res = await axios.get(`${API}/admin/orders/export.csv`, { ...authHeaders, responseType: 'blob' });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `transactions_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(language === 'fr' ? 'Export CSV téléchargé' : 'CSV export downloaded');
    } catch (error) {
      toast.error(language === 'fr' ? 'Échec de l\'export' : 'Export failed');
    } finally {
      setCsvBusy(false);
    }
  };

  const setupBoxtalWebhooks = async () => {
    setWebhooksBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/boxtal/subscriptions/setup`, {}, authHeaders);
      const active = data.subscriptions.filter(s => s.status !== 'ERROR').length;
      const errored = data.subscriptions.filter(s => s.status === 'ERROR');
      if (errored.length > 0) {
        toast.error(`${errored.length} souscription(s) en erreur: ${errored[0].errors?.[0]?.code || ''}`);
      } else {
        toast.success(language === 'fr'
          ? `Webhooks Boxtal actifs (${active}/2) — suivi automatique activé`
          : `Boxtal webhooks active (${active}/2) — auto tracking enabled`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail?.message || error.response?.data?.detail || 'Error');
    } finally {
      setWebhooksBusy(false);
    }
  };


  if (loading) {
    return (
      <div className="container-main py-8">
        <Skeleton className="h-8 w-64 mb-8" />
        <div className="grid grid-cols-4 gap-6 mb-8">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-32 rounded-xl" />)}
        </div>
        <Skeleton className="h-96 rounded-xl" />
      </div>
    );
  }

  const statCards = [
    { label: t('users'), value: stats?.total_users || 0, icon: Users, color: 'bg-blue-500', sub: `${stats?.pending_vendors || 0} ${language === 'fr' ? 'vendeurs en attente' : 'pending vendors'}` },
    { label: t('vendors'), value: stats?.total_vendors || 0, icon: Package, color: 'bg-green-500' },
    { label: t('products'), value: stats?.total_products || 0, icon: ShoppingBag, color: 'bg-purple-500', sub: `${stats?.pending_products || 0} ${language === 'fr' ? 'en attente' : 'pending'}` },
    { label: language === 'fr' ? 'Revenus' : 'Revenue', value: `€${(stats?.total_revenue || 0).toFixed(2)}`, icon: DollarSign, color: 'bg-yellow-500' },
  ];

  const pendingVendors = vendors.filter(v => v.status === 'pending');
  const pendingProducts = products.filter(p => p.status === 'pending');

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
      <div className="container-main py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-bold">{t('adminDashboard')}</h1>
            <p className="text-muted-foreground mt-1">Respectful Markets</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={() => navigate('/')} data-testid="open-vendor-from-admin">
              <ExternalLink className="w-4 h-4 mr-1.5" />
              {language === 'fr' ? 'Voir le site' : 'View site'}
            </Button>
            <Button variant="outline" size="sm" onClick={() => window.location.reload()} data-testid="admin-refresh">
              <Loader2 className="w-4 h-4 mr-1.5" />
              {language === 'fr' ? 'Actualiser' : 'Refresh'}
            </Button>
          </div>
        </div>

        {/* Alerts */}
        {(pendingVendors.length > 0 || pendingProducts.length > 0) && (
          <div className="mb-8 space-y-3">
            {pendingVendors.length > 0 && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-yellow-600" />
                <span className="text-yellow-800">
                  {pendingVendors.length} {language === 'fr' ? 'vendeurs en attente de validation' : 'vendors pending approval'}
                </span>
              </div>
            )}
            {pendingProducts.length > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-blue-600" />
                <span className="text-blue-800">
                  {pendingProducts.length} {language === 'fr' ? 'produits en attente de validation' : 'products pending approval'}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {statCards.map((stat, index) => (
            <Card key={index} className="card-dashboard">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">{stat.label}</p>
                    <p className="text-2xl font-bold mt-1">{stat.value}</p>
                    {stat.sub && <p className="text-xs text-muted-foreground mt-1">{stat.sub}</p>}
                  </div>
                  <div className={`w-12 h-12 ${stat.color} rounded-xl flex items-center justify-center`}>
                    <stat.icon className="w-6 h-6 text-white" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Main Content */}
        <Tabs defaultValue="vendors" className="space-y-6">
          <div className="overflow-x-auto -mx-2 px-2">
            <TabsList className="inline-flex w-max">
              <TabsTrigger value="vendors" data-testid="admin-vendors-tab">
                {t('vendors')}
                {pendingVendors.length > 0 && (
                  <Badge className="ml-2 bg-yellow-500">{pendingVendors.length}</Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="products" data-testid="admin-products-tab">
                {t('products')}
                {pendingProducts.length > 0 && (
                  <Badge className="ml-2 bg-blue-500">{pendingProducts.length}</Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="users" data-testid="admin-users-tab">{t('users')}</TabsTrigger>
              <TabsTrigger value="orders" data-testid="admin-orders-tab">{t('orders')}</TabsTrigger>
              <TabsTrigger value="vouchers" data-testid="admin-vouchers-tab" onClick={() => navigate('/admin/vouchers')}>
                {language === 'fr' ? 'Codes promo' : 'Vouchers'}
              </TabsTrigger>
              <TabsTrigger value="affiliates" data-testid="admin-affiliates-tab">
                {language === 'fr' ? 'Affiliés' : 'Affiliates'}
              </TabsTrigger>
              <TabsTrigger value="press" data-testid="admin-press-tab">
                <Newspaper className="w-4 h-4 mr-1" />
                {language === 'fr' ? 'Communiqués' : 'Press'}
              </TabsTrigger>
              <TabsTrigger value="testimonials" data-testid="admin-testimonials-tab">
                <MessageSquare className="w-4 h-4 mr-1" />
                {language === 'fr' ? 'Témoignages' : 'Testimonials'}
              </TabsTrigger>
              <TabsTrigger value="campaigns" data-testid="admin-campaigns-tab">
                <Mail className="w-4 h-4 mr-1" />
                {language === 'fr' ? 'Campagnes' : 'Campaigns'}
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Vendors Tab */}
          <TabsContent value="vendors">
            <Card>
              <CardHeader>
                <CardTitle>{t('vendors')}</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{language === 'fr' ? 'Entreprise' : 'Business'}</TableHead>
                      <TableHead>{language === 'fr' ? 'Pays' : 'Country'}</TableHead>
                      <TableHead>{language === 'fr' ? 'Ventes' : 'Sales'}</TableHead>
                      <TableHead>{t('rating')}</TableHead>
                      <TableHead>{language === 'fr' ? 'Statut' : 'Status'}</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {vendors.map((vendor) => (
                      <TableRow key={vendor.id}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{vendor.business_name}</p>
                            <p className="text-xs text-muted-foreground">{vendor.id.slice(0, 8)}</p>
                          </div>
                        </TableCell>
                        <TableCell>{vendor.country}</TableCell>
                        <TableCell>{vendor.total_sales}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                            {vendor.rating?.toFixed(1) || '0.0'}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={
                            vendor.status === 'active' ? 'badge-success' :
                            vendor.status === 'suspended' ? 'badge-error' : 'badge-warning'
                          }>
                            {vendor.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            {vendor.status === 'pending' && (
                              <>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-green-600"
                                  onClick={() => handleVendorStatus(vendor.id, 'active')}
                                >
                                  <CheckCircle className="w-4 h-4 mr-1" />
                                  {t('approve')}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-red-600"
                                  onClick={() => handleVendorStatus(vendor.id, 'rejected')}
                                >
                                  <XCircle className="w-4 h-4 mr-1" />
                                  {t('reject')}
                                </Button>
                              </>
                            )}
                            {vendor.status === 'active' && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-red-600"
                                onClick={() => handleVendorStatus(vendor.id, 'suspended')}
                              >
                                <Ban className="w-4 h-4 mr-1" />
                                {language === 'fr' ? 'Suspendre' : 'Suspend'}
                              </Button>
                            )}
                            {vendor.status === 'suspended' && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-green-600"
                                onClick={() => handleVendorStatus(vendor.id, 'active')}
                              >
                                <CheckCircle className="w-4 h-4 mr-1" />
                                {language === 'fr' ? 'Réactiver' : 'Reactivate'}
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Products Tab */}
          <TabsContent value="products">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0">
                <CardTitle>{t('products')}</CardTitle>
                <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleTranslateAll}
                  disabled={bulkJob?.status === 'running'}
                  data-testid="admin-translate-all-btn"
                >
                  {bulkJob?.status === 'running' ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                      {bulkJob.done ?? 0}/{bulkJob.total}
                    </>
                  ) : (
                    <>
                      <Languages className="w-4 h-4 mr-1.5" />
                      {language === 'fr' ? 'Tout traduire (IA)' : 'Translate all (AI)'}
                    </>
                  )}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    const feedUrl = `${process.env.REACT_APP_BACKEND_URL}/api/feeds/google-shopping.xml`;
                    navigator.clipboard.writeText(feedUrl).then(
                      () => toast.success(language === 'fr' ? 'URL du flux Google Shopping copiée !' : 'Google Shopping feed URL copied!'),
                      () => toast.info(feedUrl, { duration: 10000 })
                    );
                  }}
                  data-testid="google-shopping-feed-btn"
                >
                  <Copy className="w-4 h-4 mr-1.5" />
                  {language === 'fr' ? 'Flux Google Shopping' : 'Google Shopping feed'}
                </Button>
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{language === 'fr' ? 'Produit' : 'Product'}</TableHead>
                      <TableHead>{language === 'fr' ? 'Vendeur' : 'Vendor'}</TableHead>
                      <TableHead>{t('price')}</TableHead>
                      <TableHead>{t('productStock')}</TableHead>
                      <TableHead>{language === 'fr' ? 'Statut' : 'Status'}</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {products.map((product) => (
                      <TableRow key={product.id}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <img
                              src={product.images?.[0] || 'https://via.placeholder.com/50'}
                              alt={getLocalizedName(product)}
                              className="w-12 h-12 rounded-lg object-cover"
                            />
                            <div>
                              <p className="font-medium">{getLocalizedName(product)}</p>
                              {product.is_featured && (
                                <Badge className="text-xs bg-yellow-100 text-yellow-800">Featured</Badge>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>{product.vendor_name}</TableCell>
                        <TableCell>€{product.price?.toFixed(2)}</TableCell>
                        <TableCell>{product.stock}</TableCell>
                        <TableCell>
                          <Badge className={
                            product.status === 'active' ? 'badge-success' :
                            product.status === 'rejected' ? 'badge-error' : 'badge-warning'
                          }>
                            {product.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              size="icon"
                              variant="ghost"
                              title={language === 'fr' ? 'Traduire en 15 langues (IA)' : 'Translate into 15 languages (AI)'}
                              onClick={() => handleTranslateProduct(product.id)}
                              disabled={translatingId === product.id}
                              data-testid={`admin-translate-product-${product.id}`}
                            >
                              {translatingId === product.id
                                ? <Loader2 className="w-4 h-4 animate-spin" />
                                : <Languages className="w-4 h-4" />}
                            </Button>
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={() => navigate(`/product/${product.id}`)}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            {product.status === 'pending' && (
                              <>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-green-600"
                                  onClick={() => handleProductStatus(product.id, 'active')}
                                >
                                  <CheckCircle className="w-4 h-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-red-600"
                                  onClick={() => handleProductStatus(product.id, 'rejected')}
                                >
                                  <XCircle className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                            {product.status === 'active' && (
                              <Button
                                size="sm"
                                variant={product.is_featured ? 'secondary' : 'outline'}
                                onClick={() => handleToggleFeatured(product.id, !product.is_featured)}
                              >
                                <Star className={`w-4 h-4 ${product.is_featured ? 'fill-yellow-400' : ''}`} />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <Card>
              <CardHeader>
                <CardTitle>{t('users')}</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{language === 'fr' ? 'Utilisateur' : 'User'}</TableHead>
                      <TableHead>{t('email')}</TableHead>
                      <TableHead>{language === 'fr' ? 'Rôle' : 'Role'}</TableHead>
                      <TableHead>{language === 'fr' ? 'Inscrit le' : 'Joined'}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map((user) => (
                      <TableRow key={user.id}>
                        <TableCell>
                          <p className="font-medium">{user.first_name} {user.last_name}</p>
                        </TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>
                          <Badge className={
                            user.role === 'admin' ? 'bg-purple-100 text-purple-800' :
                            user.role === 'vendor' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
                          }>
                            {user.role}
                          </Badge>
                        </TableCell>
                        <TableCell>{new Date(user.created_at).toLocaleDateString()}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Orders Tab */}
          <TabsContent value="orders">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0">
                <CardTitle>{t('orders')}</CardTitle>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={exportOrdersCsv}
                    disabled={csvBusy}
                    data-testid="export-orders-csv-btn"
                  >
                    {csvBusy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Download className="w-4 h-4 mr-1.5" />}
                    {language === 'fr' ? 'Exporter CSV' : 'Export CSV'}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={webhooksBusy}
                    onClick={setupBoxtalWebhooks}
                    data-testid="boxtal-webhooks-setup-btn"
                  >
                    {webhooksBusy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Truck className="w-4 h-4 mr-1.5" />}
                    {language === 'fr' ? 'Activer webhooks Boxtal' : 'Enable Boxtal webhooks'}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{language === 'fr' ? 'Commande' : 'Order'}</TableHead>
                      <TableHead>{t('orderDate')}</TableHead>
                      <TableHead>{language === 'fr' ? 'Articles' : 'Items'}</TableHead>
                      <TableHead>{t('total')}</TableHead>
                      <TableHead>{t('orderStatus')}</TableHead>
                      <TableHead>{language === 'fr' ? 'Paiement' : 'Payment'}</TableHead>
                      <TableHead>Boxtal</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {orders.map((order) => (
                      <TableRow key={order.id}>
                        <TableCell className="font-mono text-sm">#{order.id.slice(0, 8)}</TableCell>
                        <TableCell>{new Date(order.created_at).toLocaleDateString()}</TableCell>
                        <TableCell>{order.items?.length || 0}</TableCell>
                        <TableCell>€{order.total?.toFixed(2)}</TableCell>
                        <TableCell>
                          <Badge className={
                            order.status === 'delivered' ? 'badge-success' :
                            order.status === 'shipped' ? 'badge-info' :
                            order.status === 'cancelled' ? 'badge-error' : 'badge-warning'
                          }>
                            {order.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={
                            order.payment_status === 'completed' ? 'badge-success' : 'badge-warning'
                          }>
                            {order.payment_status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {(order.payment_status === 'paid' || order.payment_status === 'completed' || order.boxtal?.shipping_order_id) ? (
                            <Button
                              size="sm"
                              variant={order.boxtal?.shipping_order_id && order.boxtal?.status !== 'CANCELLED' ? 'outline' : 'default'}
                              className={order.boxtal?.shipping_order_id && order.boxtal?.status !== 'CANCELLED' ? '' : 'btn-primary'}
                              onClick={() => setBoxtalOrder(order)}
                              data-testid={`boxtal-open-btn-${order.id}`}
                            >
                              <Truck className="w-4 h-4 mr-1.5" />
                              {order.boxtal?.shipping_order_id && order.boxtal?.status !== 'CANCELLED'
                                ? (language === 'fr' ? 'Suivi' : 'Track')
                                : (language === 'fr' ? 'Expédier' : 'Ship')}
                            </Button>
                          ) : (
                            <span className="text-xs text-muted-foreground">—</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <AdminBoxtalShipDialog
                  order={boxtalOrder}
                  open={!!boxtalOrder}
                  onOpenChange={(o) => { if (!o) setBoxtalOrder(null); }}
                  onUpdated={(orderId, patch) => setOrders((prev) => prev.map((o) => (o.id === orderId ? { ...o, ...patch } : o)))}
                />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Affiliates Tab */}
          <TabsContent value="affiliates">
            <AdminAffiliatesPanel />
          </TabsContent>

          {/* Press releases Tab */}
          <TabsContent value="press">
            <AdminPressReleasesPanel />
          </TabsContent>

          {/* Testimonials Tab */}
          <TabsContent value="testimonials">
            <AdminTestimonialsPanel />
          </TabsContent>

          {/* Email campaigns Tab */}
          <TabsContent value="campaigns">
            <AdminCampaignsPanel />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

// ============================================================================
// AdminAffiliatesPanel — embedded in /admin "Affiliates" tab
// ============================================================================
const AdminAffiliatesPanel = () => {
  const { language } = useLanguage();
  const { token } = useAuth();
  const T = (fr, en) => (language === 'fr' ? fr : en);

  const [affiliates, setAffiliates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [payoutTarget, setPayoutTarget] = useState(null);
  const [payoutForm, setPayoutForm] = useState({ amount: '', method: 'manual', stripe_connect_account_id: '', note: '' });
  const [paying, setPaying] = useState(false);
  const [payoutHistory, setPayoutHistory] = useState([]);

  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/affiliates/admin/list`, { headers: { Authorization: `Bearer ${token}` } });
      setAffiliates(data || []);
    } catch (err) {
      toast.error(T('Impossible de charger les affiliés', 'Failed to load affiliates'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    Promise.resolve().then(() => reload());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const openPayout = async (aff) => {
    setPayoutTarget(aff);
    setPayoutForm({
      amount: (aff.pending_payout || 0).toFixed(2),
      method: aff.stripe_connect_account_id ? 'stripe_connect' : 'manual',
      stripe_connect_account_id: aff.stripe_connect_account_id || '',
      note: '',
    });
    try {
      const { data } = await axios.get(`${API}/affiliates/${aff.id}/payouts`, { headers: { Authorization: `Bearer ${token}` } });
      setPayoutHistory(data || []);
    } catch {
      setPayoutHistory([]);
    }
  };

  const submitPayout = async () => {
    if (!payoutTarget) return;
    const amt = parseFloat(payoutForm.amount);
    if (isNaN(amt) || amt <= 0) {
      toast.error(T('Montant invalide', 'Invalid amount'));
      return;
    }
    setPaying(true);
    try {
      await axios.post(
        `${API}/affiliates/${payoutTarget.id}/payout`,
        {
          amount: amt,
          method: payoutForm.method,
          stripe_connect_account_id: payoutForm.stripe_connect_account_id || null,
          note: payoutForm.note || null,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(T(`Versement de €${amt.toFixed(2)} enregistré`, `Payout of €${amt.toFixed(2)} recorded`));
      setPayoutTarget(null);
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || T('Échec du versement', 'Payout failed'));
    } finally {
      setPaying(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-[#0F4C5C]" />
        </CardContent>
      </Card>
    );
  }

  const totals = affiliates.reduce(
    (acc, a) => ({
      clicks: acc.clicks + (a.total_clicks || 0),
      conversions: acc.conversions + (a.total_conversions || 0),
      earned: acc.earned + (a.total_earned || 0),
      pending: acc.pending + (a.pending_payout || 0),
      paidOut: acc.paidOut + (a.paid_out || 0),
    }),
    { clicks: 0, conversions: 0, earned: 0, pending: 0, paidOut: 0 },
  );

  const premiumCount = affiliates.filter((a) => a.tier === 'premium').length;

  return (
    <div className="space-y-4">
      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">{T('Affiliés actifs', 'Active affiliates')}</p>
            <p className="text-2xl font-bold text-[#0F4C5C]">{affiliates.length}</p>
            <p className="text-xs text-muted-foreground mt-1">{premiumCount} {T('en Premium', 'in Premium')}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">{T('Clics totaux', 'Total clicks')}</p>
            <p className="text-2xl font-bold text-[#0F4C5C]">{totals.clicks}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">{T('Conversions', 'Conversions')}</p>
            <p className="text-2xl font-bold text-emerald-700">{totals.conversions}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">{T('À payer', 'Pending payout')}</p>
            <p className="text-2xl font-bold text-[#E9C46A]">€{totals.pending.toFixed(2)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">{T('Déjà versé', 'Already paid')}</p>
            <p className="text-2xl font-bold text-emerald-700">€{totals.paidOut.toFixed(2)}</p>
          </CardContent>
        </Card>
      </div>

      {/* List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Handshake className="w-5 h-5 text-[#0F4C5C]" />
            {T('Partenaires inscrits', 'Registered partners')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {affiliates.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              {T('Aucun affilié pour le moment.', 'No affiliates yet.')}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Code</TableHead>
                    <TableHead>{T('Nom', 'Name')}</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>{T('Palier', 'Tier')}</TableHead>
                    <TableHead className="text-right">{T('Clics', 'Clicks')}</TableHead>
                    <TableHead className="text-right">{T('Ventes', 'Sales')}</TableHead>
                    <TableHead className="text-right">{T('Gagné', 'Earned')}</TableHead>
                    <TableHead className="text-right">{T('À payer', 'Pending')}</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {affiliates.map((a) => (
                    <TableRow key={a.id} data-testid={`admin-affiliate-row-${a.code}`}>
                      <TableCell className="font-mono font-bold">{a.code}</TableCell>
                      <TableCell>{a.full_name}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{a.email}</TableCell>
                      <TableCell>
                        {a.tier === 'premium' ? (
                          <Badge className="bg-[#E9C46A] text-[#0F4C5C]"><Crown className="w-3 h-3 mr-1" /> Premium 12%</Badge>
                        ) : (
                          <Badge variant="outline"><Trophy className="w-3 h-3 mr-1" /> Standard 5%</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">{a.total_clicks || 0}</TableCell>
                      <TableCell className="text-right font-semibold">{a.total_conversions || 0}</TableCell>
                      <TableCell className="text-right font-semibold">€{(a.total_earned || 0).toFixed(2)}</TableCell>
                      <TableCell className="text-right text-[#E9C46A] font-bold">€{(a.pending_payout || 0).toFixed(2)}</TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant={(a.pending_payout || 0) > 0 ? 'default' : 'outline'}
                          disabled={(a.pending_payout || 0) <= 0}
                          onClick={() => openPayout(a)}
                          data-testid={`admin-pay-affiliate-${a.code}`}
                          className={(a.pending_payout || 0) > 0 ? 'btn-primary' : ''}
                        >
                          <Banknote className="w-4 h-4 mr-1" />
                          {T('Payer', 'Pay')}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Payout dialog */}
      <Dialog open={!!payoutTarget} onOpenChange={(open) => { if (!open && !paying) setPayoutTarget(null); }}>
        <DialogContent className="max-w-lg">
          {payoutTarget && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Wallet className="w-5 h-5 text-[#0F4C5C]" />
                  {T('Verser la commission', 'Pay commission')}
                </DialogTitle>
                <DialogDescription>
                  {payoutTarget.full_name} · <code>{payoutTarget.code}</code>
                  <br />
                  {T(`En attente : `, `Pending: `)}<strong>€{(payoutTarget.pending_payout || 0).toFixed(2)}</strong>
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4">
                <div>
                  <Label htmlFor="pay-method">{T('Méthode', 'Method')}</Label>
                  <select
                    id="pay-method"
                    data-testid="payout-method-select"
                    value={payoutForm.method}
                    onChange={(e) => setPayoutForm({ ...payoutForm, method: e.target.value })}
                    className="mt-1.5 w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                  >
                    <option value="manual">{T('Manuel (virement hors-plateforme)', 'Manual (off-platform wire)')}</option>
                    <option value="stripe_connect">{T('Stripe Connect Transfer', 'Stripe Connect Transfer')}</option>
                  </select>
                </div>

                {payoutForm.method === 'stripe_connect' && (
                  <div>
                    <Label htmlFor="pay-stripe-id">Stripe Connect Account ID *</Label>
                    <Input
                      id="pay-stripe-id"
                      data-testid="payout-stripe-account-id"
                      value={payoutForm.stripe_connect_account_id}
                      onChange={(e) => setPayoutForm({ ...payoutForm, stripe_connect_account_id: e.target.value })}
                      placeholder="acct_1A2B3C..."
                      className="mt-1.5 font-mono text-sm"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      {T('Le compte Stripe Connect du partenaire (commence par acct_).', 'Partner\'s Stripe Connect account (starts with acct_).')}
                    </p>
                  </div>
                )}

                <div>
                  <Label htmlFor="pay-amount">{T('Montant (€)', 'Amount (€)')} *</Label>
                  <Input
                    id="pay-amount"
                    data-testid="payout-amount"
                    type="number"
                    step="0.01"
                    min="0.01"
                    max={payoutTarget.pending_payout || 0}
                    value={payoutForm.amount}
                    onChange={(e) => setPayoutForm({ ...payoutForm, amount: e.target.value })}
                    className="mt-1.5"
                  />
                </div>

                <div>
                  <Label htmlFor="pay-note">{T('Note (optionnel)', 'Note (optional)')}</Label>
                  <Input
                    id="pay-note"
                    data-testid="payout-note"
                    value={payoutForm.note}
                    onChange={(e) => setPayoutForm({ ...payoutForm, note: e.target.value })}
                    placeholder={T('Ex: Virement n°123 le 15/02', 'Ex: Wire #123 on 02/15')}
                    className="mt-1.5"
                  />
                </div>

                {payoutHistory.length > 0 && (
                  <div className="border-t pt-3">
                    <p className="text-sm font-semibold mb-2">{T('Historique des paiements', 'Payout history')}</p>
                    <div className="space-y-1 max-h-32 overflow-y-auto text-xs">
                      {payoutHistory.map((p) => (
                        <div key={p.id} className="flex items-center justify-between bg-slate-50 rounded px-2 py-1.5">
                          <span className="text-muted-foreground">
                            {new Date(p.executed_at).toLocaleDateString()} · {p.method}
                          </span>
                          <strong className="text-emerald-700">€{p.amount.toFixed(2)}</strong>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setPayoutTarget(null)} disabled={paying}>
                  {T('Annuler', 'Cancel')}
                </Button>
                <Button onClick={submitPayout} disabled={paying} className="btn-primary" data-testid="payout-confirm-btn">
                  {paying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Banknote className="w-4 h-4 mr-2" />}
                  {T('Confirmer le versement', 'Confirm payout')}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
